import type { Meta } from '@unhead/vue';
import { type DateArg, format, type FormatOptions } from 'date-fns';
import { getApp } from 'firebase/app';
import { memoize } from 'lodash-es';

export const gsUrl = (
  path: TemplateStringsArray | string,
  ...substitutions: unknown[]
) => {
  const p =
    typeof path === 'string' ? path : String.raw(path, ...substitutions);
  if (p.startsWith('/'))
    return `https://firebasestorage.googleapis.com/v0/b/${getApp().options.storageBucket}/o/${encodeURIComponent(p.slice(1))}?alt=media`;

  const m = p.match(/gs:\/\/([^/]+)\/(.+)/);
  if (m)
    // return `https://firebasestorage.googleapis.com/v0/b/castalianspring-de.appspot.com/o/${path}?alt=media`
    return `https://firebasestorage.googleapis.com/v0/b/${m[1]}/o/${encodeURIComponent(m[2])}?alt=media`;

  return p;
};

export function spanFirst(text: string, count: number = 1) {
  return '<span>' + text.slice(0, count) + '</span>' + text.slice(count);
}

export function spanFirstWord(text: string) {
  const index = Math.max(1, text.indexOf(' '));
  return (
    text.slice(0, index - 1) +
    '<span>' +
    text.slice(index - 1, index) +
    '</span>' +
    text.slice(index)
  );
}

export function spanLast(text: string) {
  return (
    text.slice(0, text.length - 1) +
    '<span>' +
    text.slice(text.length - 1) +
    '</span>'
  );
}

let currencyLocale = undefined as undefined | string;
let defaultCurrency = undefined as undefined | string;

export function setCurrencyDefaults(locale?: string, currency?: string) {
  currencyLocale = locale;
  defaultCurrency = currency;
}

const getCurrencyFormatter = memoize(
  (locale?: string, currency?: string) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format,
  (...args: (string | undefined)[]) =>
    args.filter((arg) => arg !== undefined).join('|'),
);

export const formatCurrency = (
  amount: number,
  currency: string | undefined = defaultCurrency,
) => getCurrencyFormatter(currencyLocale, currency)(amount);

const _getCurrencySymbol = memoize(
  (locale?: string, currency?: string) => {
    if (!currency) return undefined;
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value;
  },
  (...args: (string | undefined)[]) =>
    args.filter((arg) => arg !== undefined).join('|'),
);

export const getCurrencySymbol = (
  currency: string | undefined = defaultCurrency,
) => _getCurrencySymbol(currencyLocale, currency);

let defaultLocale = undefined as undefined | string;

export function setLocaleDefaults(locale?: string) {
  defaultLocale = locale;
}

const getHourNarrowFormat = memoize(
  (locale?: string) =>
    new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: 'hour',
      unitDisplay: 'narrow',
    }),
);

export const formatHourNarrow = (value: number) =>
  getHourNarrowFormat(defaultLocale).format(value);

const getDayNarrowFormat = memoize(
  (locale?: string) =>
    new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: 'day',
      unitDisplay: 'narrow',
    }),
);

export const formatDayNarrow = (value: number) =>
  getDayNarrowFormat(defaultLocale).format(value);

const getDayFormat = memoize(
  (locale?: string) =>
    new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: 'day',
    }),
);

export const formatDay = (value: number) =>
  getDayFormat(defaultLocale).format(value);

const getPluralRules = memoize(
  (locale?: string) => new Intl.PluralRules(locale),
);

export const plural = (
  value: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string },
) => forms[getPluralRules(defaultLocale).select(value)] ?? forms.other;

export const formatRange = (
  start: Date,
  end: Date,
  options: FormatOptions & { long?: boolean } = {},
) => {
  if (start.getUTCFullYear() !== end.getUTCFullYear())
    return `${format(start, options.long ? 'PPP' : 'PP', options)} - ${format(end, options.long ? 'PPP' : 'PP', options)}`;

  const isSameYear = start.getUTCFullYear() === new Date().getFullYear();
  const monthFormat = options.long ? 'MMMM' : 'MMM';

  if (start.getUTCMonth() !== end.getUTCMonth())
    return `${format(start, `d ${monthFormat}`, options)} - ${format(end, isSameYear ? `d ${monthFormat}` : `d ${monthFormat} Y`, options)}`;

  return `${format(start, 'd', options)} - ${format(end, isSameYear ? `d ${monthFormat}` : `d ${monthFormat} Y`, options)}`;
};

export function formatDayDistance(
  laterDate: DateArg<Date>,
  earlierDate: DateArg<Date>,
  options?: FormatOptions,
) {
  const now = Math.ceil(new Date(earlierDate).getTime() / (24 * 3600000)),
    d = Math.ceil(new Date(laterDate).getTime() / (24 * 3600000)) - now;

  if (!d) return $t`Today`;
  if (d === 1) return $t`Tomorrow`;
  if (d === -1) return $t`Yesterday`;
  if (Math.abs(d) < 7)
    return `${d > 0 ? $t`Next` : $t`Last`} ${format(laterDate, 'EEEE', options)}`;

  return undefined;
}

export function buildMetaTags(
  meta: Partial<{
    title: string;
    description: string;
    image: string;
    article: boolean;
  }>,
) {
  const tags: Meta[] = [
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
  ];

  if (meta.title) {
    tags.push(
      {
        property: 'og:title',
        content: meta.title,
      },
      {
        name: 'twitter:title',
        content: meta.title,
      },
    );
  }

  if (meta.description) {
    tags.push(
      {
        name: 'description',
        content: meta.description,
      },
      {
        property: 'og:description',
        content: meta.description,
      },
      {
        name: 'twitter:description',
        content: meta.description,
      },
    );
  }

  if (meta.image) {
    tags.push(
      {
        name: 'image',
        content: meta.image,
      },
      {
        property: 'og:image',
        content: meta.image,
      },
      {
        name: 'twitter:image',
        content: meta.image,
      },
    );
  }

  if (meta.article) {
    tags.push({
      property: 'og:type',
      content: 'article',
    });
  }

  return tags;
}
